// Cart service — abstracts the cart backend so the UI doesn't care whether
// lines live in localStorage or on the Galleria Commerce Gateway. The UI
// reads `service.lines` for fast renders and subscribes via `onChange()` to
// re-render after async ops complete.
//
// Transports:
//   - LocalCartTransport: localStorage-only. No checkout. Used in
//     visual-preview / no-backend deploys.
//   - GcgCartTransport: server-side cart via the gateway. Owns session
//     lifecycle, cart ops, and the redirect-checkout handoff. Currently
//     stubbed pending the gateway dev's endpoint-shape + auth-header
//     confirmation; will be wired when those land.
//
// Selection happens in app.js based on config: a populated `gatewayConfig`
// means GCG transport, anything else falls back to local. The two
// transports never coexist — the cart UI only reflects the active one.

const CART_STORAGE_KEY    = "kapowx_cart";

/**
 * ASK-177 S4 (Client 5g-hub item 6; card 123qy9rujru) — WHAT the shopper is told when an add is refused. The gateway's
 * 412 and 409 are DEFINITIVE answers (asking again changes nothing), so they say the reason; only no answer at all
 * (network) or the server's own failure (5xx) may say "try again in a moment". Pure.
 *   412 (connector_mode: the shop's cart is not open)   → "This shop can't take orders yet"
 *   409 not_in_this_shop · not_for_sale (no_price / outofstock / discontinued) · 404 unknown product → "Not for sale here"
 *   409 quantity_exceeds_stock                           → "Only n left" (n = remainingQuantity; 0 → "None left")
 * → { definitive, message }
 */
export function cartRefusal(result) {
	const status = Number(result?.status) || 0;
	const code = typeof result?.code === "string" ? result.code : "";
	if (status === 412) return { definitive: true, message: "This shop can't take orders yet." };
	if (status === 409 && code === "quantity_exceeds_stock") {
		const n = Number.isFinite(Number(result?.remaining)) ? Math.max(0, Math.floor(Number(result.remaining))) : null;
		return { definitive: true, message: n == null ? "No more of this one left." : n === 0 ? "None left — you have them all in your basket." : `Only ${n} left.` };
	}
	if (status === 409 || status === 404) return { definitive: true, message: "Not for sale here." };
	if (status >= 400 && status < 500) return { definitive: true, message: "Couldn't add this to your basket." };
	return { definitive: false, message: "Couldn't add to cart — please try again in a moment." };
}

/**
 * coord ASK-180 §3.10 item 1 (K-COM, D-092) — WHAT the shopper is told when the pay door refuses. Every gateway refusal
 * on the `gcg` checkout is named (§3.5a/§3.7a: `checkout_closed · payments_not_linked · seller_details_missing ·
 * cart_empty · lines_not_for_sale · email_required · bad_callback_url · bad_address · bad_note`, 502
 * `payment_initialize_failed`), so each gets its own sentence; a field refusal names the field (`field` = the form key
 * to mark). Only no answer at all / a 5xx says "try again". Pure. → { message, field, definitive }
 */
export function checkoutRefusal(result) {
	const status = Number(result?.status) || 0;
	const code = typeof result?.code === "string" ? result.code : "";
	const detail = typeof result?.detail === "string" ? result.detail : "";
	const say = (message, field = null, definitive = true) => ({ message, field, definitive });
	switch (code) {
		case "checkout_closed": return say("Checkout isn't open in this shop yet — your basket is kept for when it is.");
		case "payments_not_linked": return say("This shop can't take payments yet — the seller hasn't connected their payment account. Your basket is kept.");
		case "seller_details_missing": return say("This shop can't take payments yet — the seller hasn't finished their shop details. Your basket is kept.");
		case "cart_empty": return say("Your basket is empty — add something first.");
		case "lines_not_for_sale": return say("Something in your basket can no longer be bought here — remove it and try again.");
		case "email_required": return say("Enter an email for your receipt.", "email");
		case "bad_callback_url": return say("Checkout couldn't start from this page — reload the shop and try again.");
		case "bad_note": return say("Keep the note to 500 characters or fewer.", "note");
		case "bad_address": {
			const m = /address\.([a-zA-Z0-9]+)/.exec(detail);
			const field = m ? m[1] : null;
			const words = { name: "the recipient's name", line1: "the street address", line2: "the second address line", city: "the city or town",
				region: "the province / region", postalCode: "the postal code", country: "the country (two letters, e.g. ZA)", phone: "the phone number" };
			return say(field ? `Check ${words[field] || field} — ${/required/.test(detail) ? "it is needed" : "it doesn't look right"}.` : "Check the delivery address.", field);
		}
		case "payment_initialize_failed": return say("The payment page couldn't be opened — nothing was charged. Please try again in a moment.", null, false);
		default: break;
	}
	if (status === 401 || status === 410) return say("Your session ended — reload the shop and try again.");
	if (status >= 400 && status < 500) return say("Checkout couldn't start — nothing was charged.");
	return say("Couldn't reach checkout — nothing was charged. Please try again in a moment.", null, false);
}

/**
 * coord ASK-180 §3.10 item 3 (K-COM) — the "Checkout opens at launch" stopper shows ONLY when checkout is closed: the
 * plane's catalog flag closes every shop (production, ASK-145 §2), else the shop's own feed `commerce.checkout` decides —
 * `closed` → the stopper, `open` → the real button. Absent commerce = no opinion = the backend's own checkout (the Kapow
 * Medusa dev store), never a stopper. Pure.
 */
export function checkoutStopperShown({ forcedClosed = false, commerce = null } = {}) {
	if (forcedClosed) return true;
	return commerce?.checkout === "closed";
}

/** PortOrder status → the shopper's word. Pure. */
export function orderStatusWord(status) {
	return ({ pending: "Awaiting payment", paid: "Paid — being prepared", fulfilled: "Fulfilled", cancelled: "Cancelled", refunded: "Refunded" })[status] || "Processing";
}

/** PortMoney → "R 180.00" (ZAR prints R; other currencies their code). Pure. */
export function formatPortMoney(m) {
	if (!m || !Number.isFinite(Number(m.amount))) return "";
	const cur = m.currencyCode === "ZAR" ? "R" : (m.currencyCode || "");
	return `${cur} ${(Number(m.amount) / 100).toFixed(2)}`.trim();
}

/** The basket as it reads the moment Add is pressed: one more of the product on its line, or a new pending line. Pure. */
export function withProvisionalAdd(lines, product) {
	const pid = String(product?.kapowxProductId ?? product?.id ?? "");
	const list = Array.isArray(lines) ? lines : [];
	const i = pid ? list.findIndex((l) => String(l.kapowxProductId ?? l.id) === pid) : -1;
	if (i >= 0) return list.map((l, j) => (j === i ? { ...l, quantity: (l.quantity || 0) + 1, pending: true } : l));
	return [...list, { id: `pending:${pid}`, lineId: null, kapowxProductId: pid || null, title: product?.title || "Adding…", price: product?.price ?? "", quantity: 1, pending: true }];
}

export class CartService {
	constructor(transport) {
		this.transport = transport;
		this.lines     = transport.loadInitial();
		this._listeners = new Set();
	}

	onChange(fn) {
		this._listeners.add(fn);
		return () => this._listeners.delete(fn);
	}

	_emit() {
		for (const fn of this._listeners) {
			try { fn(this.lines); } catch (err) { console.error("[cart] listener error:", err); }
		}
	}

	get count() {
		return this.lines.reduce((n, line) => n + (line.quantity || 0), 0);
	}

	get supportsCheckout() {
		return !!this.transport.supportsCheckout;
	}

	// ASK-139 F-139c — ONE contract at the service boundary: a transport that THROWS (a refused
	// session, a network drop) is a failed op `{ok:false}`, never an unhandled rejection. Measured: GCG's
	// session create answered 401 and the add surfaced as an uncaught page error with no toast.
	async _guard(op, fn) {
		try { return await fn(); }
		catch (err) {
			console.warn(`[cart] ${op} failed:`, err?.message || err);
			return { ok: false, error: String(err?.message || err).slice(0, 200) };
		}
	}

	// ASK-159 §2.18jr (Client 5f item 4; WoW §14.5 "add-to-cart" = OPTIMISTIC) — the basket moves the moment the shopper
	// presses Add (a provisional line, `pending: true`), and the server's answer replaces it; a refused add puts the basket
	// back as it was — unless a newer op already replaced the lines, whose answer then stands.
	async addItem(product) {
		const before = this.lines;
		let provisional = null;
		const op = optimistic({
			apply: () => { provisional = withProvisionalAdd(before, product); this.lines = provisional; this._emit(); },
			revert: () => { if (this.lines === provisional) { this.lines = before; this._emit(); } },
		});
		const result = await this._guard("add", () => this.transport.addItem(product));
		op.settle(!!result.ok);
		if (result.ok) {
			this.lines = result.lines ?? this.lines;
			this._emit();
		}
		return result;
	}

	async removeItem(lineId) {
		const result = await this._guard("remove", () => this.transport.removeItem(lineId));
		if (result.ok) {
			this.lines = result.lines ?? this.lines;
			this._emit();
		}
		return result;
	}

	async clear() {
		const result = await this._guard("clear", () => this.transport.clear());
		if (result.ok) {
			this.lines = [];
			this._emit();
		}
		return result;
	}

	/** `details` = the `gcg` adapter's checkout body (address · note · email · callbackUrl); absent = the legacy handoff. */
	async checkout(details = undefined) {
		if (!this.supportsCheckout) {
			return { ok: false, error: "no_checkout_support" };
		}
		return this._guard("checkout", () => this.transport.checkout(details));
	}

	/** OrderPort.listOrders for the shopper in the active shop (K-COM item 2); `[]` where the backend has no ledger door. */
	async listOrders(query = {}) {
		if (typeof this.transport.listOrders !== "function") return { ok: true, items: [], status: 0 };
		return this.transport.listOrders(query);
	}

	// Hydrate from the active transport. Local: no-op (already loaded in
	// constructor). GCG: fetches the existing cart if a session is cached.
	// Called once by app.js at boot; safe to call again.
	async hydrate() {
		if (typeof this.transport.hydrate !== "function") return;
		const result = await this._guard("hydrate", () => this.transport.hydrate());
		if (result?.ok && result.lines) {
			this.lines = result.lines;
			this._emit();
		}
	}

	// Returns the authenticated customer's profile from /customers/me, or
	// null when anonymous / unavailable. Used by CheckoutOverlay to
	// pre-fill the address form for repeat customers. Profile is server-
	// persisted on every successful checkout, so the second order onwards
	// arrives with name + address already populated.
	async getCustomerProfile() {
		if (typeof this.transport.getCustomerProfile !== "function") return null;
		return this.transport.getCustomerProfile();
	}

	// Sync the local mirror after a paid order: the gateway already cleared the bought lines when the payment
	// settled (GCG webhook → paid), so calling clear() again would be a wasted round-trip.
	markCleared() {
		this.lines = [];
		this._emit();
	}

	// ASK-139 F-139c — commerce is a SHOP fact (the feed's `commerce`), so the backend is bound on
	// shop entry, not once at boot. Swapping keeps the listeners (the UI never re-subscribes) and
	// re-reads the cart from the new backend: what the shopper sees is that shop's cart, never the
	// previous backend's lines.
	async useTransport(transport) {
		this.transport = transport;
		this.lines = transport.loadInitial();
		this._emit();
		await this.hydrate();
	}
}

// ── LocalCartTransport ─────────────────────────────────────────────────────
// localStorage-backed. No checkout because there's no backend to redirect to.
// The cart still works for browse-and-collect previews; the UI just hides the
// checkout button when supportsCheckout is false.

export class LocalCartTransport {
	supportsCheckout = false;

	loadInitial() {
		try {
			const raw = localStorage.getItem(CART_STORAGE_KEY);
			return raw ? JSON.parse(raw) : [];
		} catch {
			return [];
		}
	}

	_save(lines) {
		try { localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(lines)); }
		catch { /* storage unavailable — cart lives only in memory */ }
	}

	async addItem(product) {
		const lines = this.loadInitial();
		const existing = lines.find((l) => String(l.id) === String(product.id));
		if (existing) {
			existing.quantity += 1;
		} else {
			lines.push({
				id:       String(product.id),
				title:    product.title || `Product ${product.id}`,
				price:    product.price || "",
				quantity: 1,
			});
		}
		this._save(lines);
		return { ok: true, lines };
	}

	async removeItem(lineId) {
		const lines = this.loadInitial().filter((l) => String(l.id) !== String(lineId));
		this._save(lines);
		return { ok: true, lines };
	}

	async clear() {
		this._save([]);
		return { ok: true, lines: [] };
	}

	async checkout() {
		return { ok: false, error: "no_checkout_support" };
	}
}

// ── GcgCartTransport ──────────────────────────────────────────────────────
// Server-side cart via the Galleria Commerce Gateway. Owns:
//   - Session lifecycle: lazy create on first cart op, persist token in
//     localStorage, refresh on 401, rotate on every successful response.
//   - Cart ops via REST + Authorization: Bearer <sessionToken>.
//   - Checkout returning a single-use 5-min redirect URL.
//
// Auth is asymmetric:
//   Outbound (runtime → GCG): Authorization: Bearer <sessionToken>
//   Inbound  (GCG → runtime): X-Galleria-Session-Token + X-Galleria-Session-
//                             Expires-At — fresh JWT to swap to. The old
//                             one still works until DB expiry, but rotating
//                             is what extends the 24h refresh-on-activity.
//
// CORS uses Allow-Origin: * with no credentials. Don't set
// `credentials: 'include'` — the wildcard origin makes the browser reject.

export class GcgCartTransport {
	supportsCheckout = true;

	constructor({ baseUrl, shopId, apiNamespace, firebaseIdToken, getFreshIdToken }) {
		this.baseUrl         = baseUrl.replace(/\/$/, "");
		this.shopId          = shopId;
		this.apiNamespace    = apiNamespace || "kapowx/v1";
		this.firebaseIdToken = firebaseIdToken || null;  // attached to session-create when present
		this.getFreshIdToken = getFreshIdToken || null;  // E3 — live provider; the snapshot above stales after 1h
		this._session        = this._loadSession();
	}

	get _apiBase() {
		return `${this.baseUrl}/api/${this.apiNamespace}`;
	}

	_loadSession() {
		return storedShopSession(this.shopId);
	}

	_saveSession(session) {
		this._session = session;
		try { localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session)); } catch {}
	}

	_clearSession() {
		this._session = null;
		try { localStorage.removeItem(SESSION_STORAGE_KEY); } catch {}
	}

	loadInitial() {
		// Server is the source of truth — never resurrect a stale local mirror.
		// Cart contents come from `hydrate()` on app boot.
		return [];
	}

	// ── Session lifecycle ─────────────────────────────────────────────────

	async _createSession() {
		// Attach Firebase ID token if the customer is signed in. GCG iter-5a
		// validates it, links the session to a customers row keyed by the
		// Firebase UID, and returns isAuthenticated:true in the response.
		// Anonymous sessions (no token) still work — used for dev / preview
		// deploys and the brief window before sign-in completes.
		const headers = { "Content-Type": "application/json" };
		// E3 — prefer a fresh token; the mount-time snapshot expires after 1h.
		const tok = (this.getFreshIdToken ? await this.getFreshIdToken().catch(() => null) : null) || this.firebaseIdToken;
		if (tok) {
			headers["Authorization"] = `Bearer ${tok}`;
		}
		const resp = await fetch(`${this._apiBase}/shops/${encodeURIComponent(this.shopId)}/sessions`, {
			method:  "POST",
			headers,
			body:    "{}",
		});
		if (!resp.ok) {
			throw new Error(`session create failed: ${resp.status}`);
		}
		const data = await resp.json();
		const session = {
			sessionId:       data.sessionId,
			sessionToken:    data.sessionToken,
			expiresAt:       data.expiresAt,
			shopId:          data.shopId,
			isAuthenticated: !!data.isAuthenticated,
		};
		this._saveSession(session);
		return session;
	}

	async _ensureSession() {
		if (this._session?.sessionToken) return this._session;
		return this._createSession();
	}

	// Authenticated request with token rotation + 401 retry. Caller passes
	// path (relative to apiBase) and optional JSON body. Returns the raw
	// fetch Response so callers can branch on status before parsing.
	async _authedFetch(method, path, body) {
		await this._ensureSession();
		const buildOpts = (token) => {
			const opts = {
				method,
				headers: {
					"Authorization": `Bearer ${token}`,
					...(body !== undefined ? { "Content-Type": "application/json" } : {}),
				},
			};
			if (body !== undefined) opts.body = JSON.stringify(body);
			return opts;
		};

		let resp = await fetch(`${this._apiBase}${path}`, buildOpts(this._session.sessionToken));

		// 401 → token's stale, mint a new session and retry once.
		if (resp.status === 401) {
			this._clearSession();
			const fresh = await this._createSession();
			resp = await fetch(`${this._apiBase}${path}`, buildOpts(fresh.sessionToken));
		}

		// Refresh-on-activity: gateway ships a fresh token in headers on
		// every successful op. Swap to it for the next request — old still
		// works until DB expiry but skipping rotation drifts toward the 24h.
		if (resp.ok) {
			const newToken  = resp.headers.get("X-Galleria-Session-Token");
			const newExpiry = resp.headers.get("X-Galleria-Session-Expires-At");
			if (newToken && this._session) {
				this._saveSession({
					...this._session,
					sessionToken: newToken,
					...(newExpiry ? { expiresAt: newExpiry } : {}),
				});
			}
		}
		return resp;
	}

	// Map server EnrichedCartState lines → CartService line shape. UI reads
	// id/title/price/quantity; we keep server-only fields (lineId is server-
	// issued and used for remove/update; thumbnail + lineTotal for richer
	// rendering when the UI grows).
	_adaptCart(state) {
		return (state?.lines || []).map((l) => ({
			id:              l.lineId,
			lineId:          l.lineId,
			kapowxProductId: l.kapowxProductId,
			title:           l.title,
			price:           l.unitPrice,
			quantity:        l.quantity,
			thumbnail:       l.thumbnail,
			lineTotal:       l.lineTotal,
		}));
	}

	// ── Cart ops ──────────────────────────────────────────────────────────

	async hydrate() {
		// Called at app boot — pulls the existing cart if a session is cached.
		// No-op when there's no session yet (empty cart, no round-trip).
		if (!this._session) return { ok: true, lines: [] };
		const resp = await this._authedFetch("GET", `/sessions/${this._session.sessionId}/cart`);
		if (!resp.ok) {
			// 404 / 401 / etc — discard the bad session and start fresh on next op.
			this._clearSession();
			return { ok: true, lines: [] };
		}
		const state = await resp.json();
		return { ok: true, lines: this._adaptCart(state) };
	}

	async addItem(product) {
		const kapowxProductId = product?.kapowxProductId || product?.id;
		if (!kapowxProductId) {
			return { ok: false, error: "product missing kapowxProductId" };
		}
		await this._ensureSession();
		const resp = await this._authedFetch("POST", `/sessions/${this._session.sessionId}/cart`, {
			kapowxProductId, quantity: 1,
		});
		if (!resp.ok) {
			const err = await resp.text().catch(() => "");
			let body = null;
			try { body = JSON.parse(err); } catch { /* not JSON: the status alone decides */ }
			// the gateway's refusal, kept structured (S4): status + its reason code + the stock it names
			return { ok: false, status: resp.status, code: typeof body?.error === "string" && /^[a-z_]+$/.test(body.error) ? body.error : (resp.status === 412 ? "connector_mode" : null),
				detail: body?.detail ?? null, remaining: body?.remainingQuantity ?? null, error: `add ${resp.status}: ${err.slice(0, 200)}` };
		}
		return { ok: true, lines: this._adaptCart(await resp.json()) };
	}

	async removeItem(lineId) {
		await this._ensureSession();
		const resp = await this._authedFetch("DELETE", `/sessions/${this._session.sessionId}/cart/${encodeURIComponent(lineId)}`);
		if (!resp.ok) {
			const err = await resp.text().catch(() => "");
			return { ok: false, error: `remove ${resp.status}: ${err.slice(0, 200)}` };
		}
		return { ok: true, lines: this._adaptCart(await resp.json()) };
	}

	async clear() {
		// No bulk-clear endpoint — fetch state, iterate DELETEs. N round-trips
		// is fine at our cart sizes; can ask the gateway dev for a bulk endpoint
		// if it ever bites.
		await this._ensureSession();
		const getResp = await this._authedFetch("GET", `/sessions/${this._session.sessionId}/cart`);
		if (!getResp.ok) {
			return { ok: false, error: `clear:get ${getResp.status}` };
		}
		const lineIds = (await getResp.json()).lines?.map((l) => l.lineId) || [];
		let lastState = null;
		for (const lineId of lineIds) {
			const r = await this._authedFetch("DELETE", `/sessions/${this._session.sessionId}/cart/${encodeURIComponent(lineId)}`);
			if (!r.ok) return { ok: false, error: `clear:remove ${r.status}` };
			lastState = await r.json();
		}
		return { ok: true, lines: lastState ? this._adaptCart(lastState) : [] };
	}

	/**
	 * coord ASK-180 §3.10 item 1 (K-COM, D-092) — CheckoutPort.createCheckoutSession against GCG's live wire.
	 * `details` (the `gcg` adapter's body, GcgCheckoutBody): `{ email?, callbackUrl?, address?, note? }`; an empty call is
	 * the legacy redirect handoff (a Woo-plugin shop). Pay = the hosted redirect to `url` (Paystack's page — no card field
	 * ever reaches GG, eComm rule 8). → `{ ok:true, kind, url, session: PortCheckoutSession, orderId, reference,
	 * disclosure, sellerOfRecord, total, supportEmail, returnsPolicyUrl }` | `{ ok:false, status, code, detail }`.
	 */
	async checkout(details = undefined) {
		await this._ensureSession();
		const resp = await this._authedFetch("POST", `/sessions/${this._session.sessionId}/checkout`, details && typeof details === "object" ? details : {});
		if (!resp.ok) {
			const text = await resp.text().catch(() => "");
			let body = null;
			try { body = JSON.parse(text); } catch { /* not JSON: the status alone decides */ }
			return { ok: false, status: resp.status, code: typeof body?.error === "string" && /^[a-z_]+$/.test(body.error) ? body.error : null,
				detail: typeof body?.detail === "string" ? body.detail : null, error: `checkout ${resp.status}: ${text.slice(0, 200)}` };
		}
		const data = await resp.json();
		if (data.kind !== "redirect" || !data.url) {
			return { ok: false, status: resp.status, code: "unexpected_checkout_response", error: `unexpected checkout response: ${JSON.stringify(data).slice(0, 200)}` };
		}
		// the port's PortCheckoutSession, read off the gateway's own words (GcgCheckoutResponse)
		const session = data.mode ? {
			sessionId: data.sessionId, cartId: data.cartId, mode: data.mode, redirectUrl: data.redirectUrl || data.url,
			clientSecret: data.clientSecret, provider: data.provider || null, sellerOfRecord: data.sellerOfRecord || null,
		} : null;
		return { ok: true, kind: data.kind, url: data.url, expiresAt: data.expiresAt, lineCount: data.lineCount, session,
			orderId: data.orderId ?? null, reference: data.reference ?? null, disclosure: data.disclosure ?? null,
			sellerOfRecord: data.sellerOfRecord ?? null, total: data.total ?? null,
			supportEmail: data.supportEmail ?? null, returnsPolicyUrl: data.returnsPolicyUrl ?? null };
	}

	/** OrderPort.listOrders for this shopper in this shop (`GET /sessions/:id/orders[?reference=]`) — PortOrder[]. With
	 *  `reference` (Paystack's callback) GCG settles a still-pending order by Paystack verify before answering, so the
	 *  confirmation never says paid on the redirect alone. Never throws. → { ok, items, status } */
	async listOrders({ reference = null } = {}) {
		if (!this._session?.sessionId) return { ok: true, items: [], status: 0 };
		try {
			const q = reference ? `?reference=${encodeURIComponent(reference)}` : "";
			const resp = await this._authedFetch("GET", `/sessions/${this._session.sessionId}/orders${q}`);
			if (!resp.ok) return { ok: false, items: [], status: resp.status };
			const body = await resp.json();
			return { ok: true, items: Array.isArray(body?.items) ? body.items : [], status: resp.status };
		} catch (err) {
			return { ok: false, items: [], status: 0, error: String(err?.message || err) };
		}
	}

	/** The return from Paystack (`?reference=`): the order that reference names, as GCG reads it after verify.
	 *  → { ok:true, order } (any status — the caller words pending vs paid) | { ok:false, error } */
	async completeAfterReturn(reference) {
		if (!reference) return { ok: false, error: "no_reference" };
		const r = await this.listOrders({ reference });
		const order = r.items.find((o) => o?.reference === reference) || null;
		return order ? { ok: true, order } : { ok: false, error: r.ok ? "order_not_found" : `orders_${r.status || "unreachable"}` };
	}

	// ── Customer profile (autofill source for checkout) ───────────────────
	// Returns { email, displayName, approved, defaultBillingAddress,
	// defaultShippingAddress } for the authenticated customer behind the
	// active session, or null for anonymous sessions / when the call fails.
	// ASK-167 G-c / Client 4w — the customer's preferences door (`PATCH /customers/me { avatarKind }`, GCG 6t). Feature-
	// detected by the caller: 404 = the door is not on this plane yet. Returns { ok, status, body } — never throws on HTTP.
	async patchCustomerPreferences(body) {
		const resp = await this._authedFetch("PATCH", `/customers/me`, body);
		let parsed = null;
		try { parsed = await resp.json(); } catch { /* empty body */ }
		if (resp.ok) this._customerProfile = undefined;   // the next read sees the new preference
		return { ok: resp.ok, status: resp.status, body: parsed };
	}

	// Cached on the instance after the first successful fetch — the profile
	// only changes when the customer completes another order (server
	// persists the updated addresses), so a page-lifetime cache is fine.
	async getCustomerProfile() {
		if (!this._session?.isAuthenticated) return null;  // skip the round-trip for anon
		if (this._customerProfile !== undefined) return this._customerProfile;
		try {
			const resp = await this._authedFetch("GET", `/customers/me`);
			if (resp.status === 404) {
				// Server says no profile yet (first session for this customer,
				// or anon despite our flag). Cache the null and move on.
				this._customerProfile = null;
				return null;
			}
			if (!resp.ok) {
				console.warn(`[cart] /customers/me ${resp.status} — falling back to local autofill`);
				return null;  // don't cache failure; retry on next call
			}
			this._customerProfile = await resp.json();
			return this._customerProfile;
		} catch (err) {
			console.warn("[cart] /customers/me fetch failed:", err);
			return null;
		}
	}

}

import { PortCartTransport } from "./commerce/port-cart-transport.js";
import { optimistic } from "./_loading.js";   // §2.18jr: add-to-cart is the 4-way standard's OPTIMISTIC form
import { SESSION_STORAGE_KEY, storedShopSession } from "./_shop-session.js";   // shared with the telemetry transport (ASK-145 §18.10)
import { devMedusaEnabled } from "./_features.js";   // ASK-145: the dev money loop is a PLANE flag, not a hostname
import { MedusaStoreAdapter } from "./commerce/medusa-adapter.js";

// Kapow Type B Medusa instance (ASK-055 — base URL + publishable key are
// browser-safe by design). Used by the dev test flag below; the wizard-
// provisioned shop config carries its own copy when Type B onboarding lands.
const KAPOW_MEDUSA_DEV = {
	baseUrl: "https://medusa-dev-pi53kq6zba-uc.a.run.app",
	publishableKey: "pk_19575cd08d5a2f8600fc6b6ea27843e9824bcf07cbb1f80a7df91ed08193a42e",
	salesChannelId: "sc_01KTVFK67H3M381QAXTSJX1D5G",
	shopId: "kapowx-coza",
};

// ── Factory ───────────────────────────────────────────────────────────────
// Picks the transport: (1) Medusa port adapter when the shop's commerce
// config says so OR the dev test flag ?medusaShop=1 is set on a dev host
// (DECISION-035v2 — the head reads port-typed objects; this is the selection
// seam); (2) GCG transport when a gateway is configured; (3) local fallback.

function medusaConfigFor(config, shopCommerce = null) {
	// 🔴 ASK-139 F-139c (D6) — a shop that NAMES a non-Medusa adapter never reaches the dev default
	// below, and so never reaches its substitute-the-first-product fallback (deferrals row 84): adding
	// Saga #1 must put Saga #1 in the cart. Absent = no opinion → today's dev money loop is unchanged.
	if (shopCommerce?.adapter && shopCommerce.adapter !== "medusa") return null;
	if (config?.commerce?.adapter === "medusa" && config.commerce.medusa) return { ...config.commerce.medusa, shopId: config.commerce.storeRef || config.commerce.medusa.shopId };
	try {
		const host = window.location.hostname;
		const isDevHost = host === "127.0.0.1" || host === "localhost" || host.startsWith("dev.") || host.endsWith(".web.app");
		// 🔴 ASK-145 — the hostname cannot tell the planes apart: production's default URL is
		// `shopthegalaxy.web.app`. Only a plane that declares the dev money loop reaches the dev store.
		if (isDevHost && devMedusaEnabled()) {
			// STICKY for the session: the Paystack redirect returns to the bare
			// callback URL (no query), which silently dropped the flag mid-test —
			// the return leg then ran on the wrong transport (no completion, no
			// confirmation). Once seen, the flag persists until the tab closes.
			const param = new URLSearchParams(window.location.search).get("medusaShop");
			if (param === "1") { try { sessionStorage.setItem("kapowx_medusa_dev", "1"); } catch { /* noop */ } }
			if (param === "0") { try { sessionStorage.removeItem("kapowx_medusa_dev"); } catch { /* noop */ } }
			let sticky = null;
			try { sticky = sessionStorage.getItem("kapowx_medusa_dev"); } catch { /* noop */ }
			if (param === "1" || sticky === "1") return KAPOW_MEDUSA_DEV;
			// DEFAULT ON for dev hosts (2026-06-12): the legacy GCG cart's WP-era
			// /sessions endpoint 404s on the gateway, so flagless dev users got a
			// silently broken add-to-cart (human-tester finding T-001 #5). The
			// working money loop IS the dev experience now; ?medusaShop=0 opts out.
			if (param !== "0" && sticky !== "0") return KAPOW_MEDUSA_DEV;
		}
	} catch { /* non-browser */ }
	return null;
}

/**
 * 🔴 ASK-139 F-139c (D6) — WHICH BACKEND A SHOP'S CART LIVES IN, from the shop's own facts.
 *
 *   feed `commerce.adapter: "gcg"`      → the GCG cart, keyed by this shop
 *   feed `commerce.adapter: "medusa"`   → the shop's Medusa (the boot/config path)
 *   no `commerce`, and the shop IS the Kapow Medusa dev store (feed `shopId` = its store ref)
 *                                       → the dev Medusa default, unchanged (the M6 money loop)
 *   no `commerce`, any OTHER shop       → the GCG cart, keyed by this shop
 *
 * The last line is the fix. The dev Medusa default used to serve EVERY shop on a dev host, and its
 * substitute-the-first-product fallback (deferrals row 84) put "Kapow Test Comic" in the cart when a
 * shopper added Spiderman on store-test-2 (measured 2026-10-09, probe-phone-walk P7). A shop that is
 * not the Kapow Medusa store has no Medusa catalogue to resolve against, so routing it there could
 * only ever substitute. Now it reaches its own backend, which holds the item or says it cannot.
 */
export function cartTransportKey(shopId, shopCommerce = null, feedShopId = null) {
	if (shopCommerce?.adapter === "gcg") return `gcg:${shopId}`;
	if (shopCommerce?.adapter === "medusa") return "default";
	if (!feedShopId || feedShopId === KAPOW_MEDUSA_DEV.shopId) return "default";
	return `gcg:${shopId}`;
}

/** ASK-139 F-139c — the transport the key above names, for one shop. */
export function cartTransportFor(config, shopId, shopCommerce = null, feedShopId = null) {
	const gw = config?.gatewayConfig;
	if (cartTransportKey(shopId, shopCommerce, feedShopId) !== "default" && gw?.baseUrl) return new GcgCartTransport({ ...gw, shopId });
	return createCartService(config, shopCommerce).transport;
}

export function createCartService(config, shopCommerce = null) {
	const medusaCfg = medusaConfigFor(config, shopCommerce);
	if (medusaCfg) {
		const adapter = new MedusaStoreAdapter({
			...medusaCfg,
			sellerOfRecord: { storeName: config?.commerce?.storeName || "Kapow", jurisdiction: config?.commerce?.jurisdiction || "ZA" },
		});
		return new CartService(new PortCartTransport({
			port: adapter, shopId: medusaCfg.shopId,
			customerEmail: config?.customerEmail || null,
			devFallbackVariant: medusaCfg === KAPOW_MEDUSA_DEV,   // only under the dev flag
		}));
	}
	const gw = config?.gatewayConfig;
	if (gw?.baseUrl && gw?.shopId) {
		return new CartService(new GcgCartTransport(gw));
	}
	return new CartService(new LocalCartTransport());
}
